import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Link2, RefreshCw, Trash2, Users, Images, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { fetchSocialStats } from "@/lib/social.functions";
import { socialAccountsQuery, formatCount, type SocialAccount } from "@/lib/people";
import { SOCIAL_LABELS } from "@/components/socials";
import {
  PLATFORM_CATALOG,
  PLATFORM_GROUP_LABELS,
  CATALOG_LABELS,
  platformIcon,
  isMusicPlatform,
  buildPlatformUrl,
  platformDef,
} from "@/lib/social-platforms";
import { useT } from "@/i18n";

export function SocialAccountsPanel({ userId }: { userId: string | undefined }) {
  const queryClient = useQueryClient();
  const { t } = useT();
  const fetchStats = useServerFn(fetchSocialStats);
  const { data: accounts } = useQuery(socialAccountsQuery(userId));
  const [platform, setPlatform] = useState("instagram");
  const [handleValue, setHandleValue] = useState("");
  const [busy, setBusy] = useState(false);

  const def = platformDef(platform);
  const grouped = useMemo(
    () =>
      (["social", "music", "other"] as const).map((group) => ({
        group,
        items: PLATFORM_CATALOG.filter((p) => p.group === group),
      })),
    [],
  );

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["social-accounts", userId] });
  }

  async function save(platformKey: string, link: string, existing?: SocialAccount) {
    if (!userId) return;
    setBusy(true);
    try {
      const stats = await fetchStats({ data: { url: link } }).catch(() => null);
      const { error } = await supabase.from("social_accounts").upsert(
        {
          user_id: userId,
          platform: platformKey,
          url: stats?.url ?? link,
          handle: stats?.handle ?? existing?.handle ?? null,
          followers: stats?.followers ?? existing?.followers ?? null,
          posts: stats?.posts ?? existing?.posts ?? null,
          engagement_rate: stats?.engagement_rate ?? existing?.engagement_rate ?? null,
          avatar_url: stats?.avatar_url ?? existing?.avatar_url ?? null,
          title: stats?.title ?? existing?.title ?? null,
          verified: stats?.verified || existing?.verified || false,
          source: stats?.source === "auto" ? "auto" : (existing?.source ?? "manual"),
          last_synced_at: new Date().toISOString(),
        },
        { onConflict: "user_id,platform" },
      );
      if (error) throw error;
      setHandleValue("");
      await refresh();
      toast.success(
        stats?.followers
          ? t("account.social.savedLive", { count: formatCount(stats.followers) })
          : t("account.social.saved"),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("account.social.saveError"));
    } finally {
      setBusy(false);
    }
  }

  function add() {
    const built = buildPlatformUrl(platform, handleValue);
    if ("error" in built) {
      toast.error(built.error);
      return;
    }
    void save(platform, built.url);
  }

  async function remove(account: SocialAccount) {
    await supabase.from("social_accounts").delete().eq("id", account.id);
    await refresh();
  }

  return (
    <section className="card-surface space-y-5 p-6">
      <div>
        <h2 className="font-display text-xl font-bold">{t("account.social.title")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("account.social.desc")}
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-[minmax(0,14rem)_1fr_auto]">
        <div className="space-y-1">
          <Label htmlFor="social-platform" className="sr-only">
            {t("account.social.service")}
          </Label>
          <select
            id="social-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
          >
            {grouped.map(({ group, items }) => (
              <optgroup key={group} label={PLATFORM_GROUP_LABELS[group]}>
                {items.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="social-url" className="sr-only">
            {t("account.social.handle")}
          </Label>
          <div className="flex h-10 items-center overflow-hidden rounded-lg border border-input bg-background">
            {def?.prefix && (
              <span className="hidden max-w-[13rem] shrink-0 truncate border-r border-input bg-muted px-3 text-xs text-muted-foreground sm:block sm:leading-10">
                {def.prefix.replace(/^https:\/\//, "")}
              </span>
            )}
            <Input
              id="social-url"
              value={handleValue}
              placeholder={def?.placeholder ?? "yourhandle"}
              onChange={(e) => setHandleValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              className="h-full border-0 bg-transparent focus-visible:ring-0"
            />
          </div>
        </div>
        <Button variant="hot" disabled={busy || !handleValue.trim()} onClick={add}>
          <Link2 className="size-4" /> {busy ? t("account.social.saving") : t("account.social.add")}
        </Button>
      </div>
      {def?.requiresHint && (
        <p className="-mt-2 text-xs text-muted-foreground">{def.requiresHint}</p>
      )}


      <div className="grid gap-3 sm:grid-cols-2">
        {(accounts ?? []).map((a) => (
          <div key={a.id} className="rounded-xl border border-border p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="flex items-center gap-1.5 font-display font-semibold">
                  {(() => {
                    const Icon = platformIcon(a.platform);
                    return <Icon className="size-4 shrink-0 text-primary" />;
                  })()}
                  {CATALOG_LABELS[a.platform] ?? SOCIAL_LABELS[a.platform] ?? a.platform}
                  {a.verified && <BadgeCheck className="size-4 text-primary" />}
                </p>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                  {a.handle ? `@${a.handle}` : a.url}
                </a>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("account.social.refresh")}
                  disabled={busy}
                  onClick={() => void save(a.platform, a.url, a)}

                >
                  <RefreshCw className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("account.social.remove")}
                  onClick={() => void remove(a)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
            {!isMusicPlatform(a.platform) && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <p className="flex items-center gap-2 text-sm font-medium">
                <Users className="size-4 shrink-0 text-primary" />
                {a.followers != null ? formatCount(a.followers) : "—"}
                <span className="text-xs font-normal text-muted-foreground">
                  {t("account.social.followers")}
                </span>
              </p>
              <p className="flex items-center gap-2 text-sm font-medium">
                <Images className="size-4 shrink-0 text-signal" />
                {a.posts != null ? formatCount(a.posts) : "—"}
                <span className="text-xs font-normal text-muted-foreground">
                  {t("account.social.posts")}
                </span>
              </p>
            </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              {a.engagement_rate != null && (
                <span>{t("account.social.engagement", { rate: a.engagement_rate })}</span>
              )}
              {t("account.social.readAuto")}
              {a.last_synced_at
                ? t("account.social.updated", {
                    date: new Date(a.last_synced_at).toLocaleDateString(),
                  })
                : ""}
            </p>
          </div>
        ))}
        {!accounts?.length && (
          <p className="text-sm text-muted-foreground">{t("account.social.empty")}</p>
        )}
      </div>
    </section>
  );
}
